import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Megaphone, Search, Users, Mail, ArrowUpRight, ArrowLeft,
  Loader2, Sparkles, Zap, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, AlertTriangle, Copy, ExternalLink,
  Trash2, RefreshCw, Download, Filter,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const SENIORITY_OPTIONS = [
  { value: 'c_suite', label: 'C-Suite' },
  { value: 'vp', label: 'VP' },
  { value: 'director', label: 'Director' },
  { value: 'manager', label: 'Manager' },
  { value: 'senior', label: 'Senior' },
];

const EMPLOYEE_RANGES = [
  { value: '1,10', label: '1-10' },
  { value: '11,50', label: '11-50' },
  { value: '51,200', label: '51-200' },
  { value: '201,500', label: '201-500' },
  { value: '501,1000', label: '501-1000' },
  { value: '1001,5000', label: '1001-5000' },
  { value: '5001,10000', label: '5001+' },
];

export default function MarketingCampaignDetailPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();

  // Campaign state
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);

  // Search criteria (editable)
  const [criteria, setCriteria] = useState({
    person_titles: '',
    person_locations: '',
    person_seniorities: [],
    person_not_titles: '',
    q_organization_name: '',
    organization_locations: '',
    organization_num_employees_ranges: [],
    keywords: '',
  });

  // Discovery state
  const [discovering, setDiscovering] = useState(false);
  const [discoveryPage, setDiscoveryPage] = useState(1);
  const [discoveryResult, setDiscoveryResult] = useState(null);

  // Leads state
  const [leads, setLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsStats, setLeadsStats] = useState({});
  const [leadsSearch, setLeadsSearch] = useState('');
  const [leadsFilter, setLeadsFilter] = useState('');
  const [loadingLeads, setLoadingLeads] = useState(false);

  // Enrichment state
  const [enriching, setEnriching] = useState(false);
  const [enrichProgress, setEnrichProgress] = useState(null);
  const enrichPollRef = useRef(null);

  // Selection
  const [selected, setSelected] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Dialogs
  const [deepEnrichTarget, setDeepEnrichTarget] = useState(null);
  const [deepEnriching, setDeepEnriching] = useState(false);
  const [showPromote, setShowPromote] = useState(false);
  const [promoting, setPromoting] = useState(false);

  // CSV import
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [importing, setImporting] = useState(false);

  // Fetch campaign
  const fetchCampaign = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/marketing/campaigns/${campaignId}`, { withCredentials: true });
      setCampaign(res.data);
      // Populate criteria from campaign
      const sc = res.data.search_criteria || {};
      setCriteria({
        person_titles: (sc.person_titles || []).join(', '),
        person_locations: (sc.person_locations || []).join(', '),
        person_seniorities: sc.person_seniorities || [],
        person_not_titles: (sc.person_not_titles || []).join(', '),
        q_organization_name: sc.q_organization_name || '',
        organization_locations: (sc.organization_locations || []).join(', '),
        organization_num_employees_ranges: sc.organization_num_employees_ranges || [],
        keywords: sc.keywords || '',
      });
    } catch (err) {
      toast.error('Failed to load campaign');
      navigate('/admin/marketing');
    } finally {
      setLoading(false);
    }
  }, [campaignId, navigate]);

  // Fetch leads
  const fetchLeads = useCallback(async (page = 1) => {
    setLoadingLeads(true);
    try {
      const params = new URLSearchParams({ page, per_page: 50 });
      if (leadsSearch) params.append('search', leadsSearch);
      if (leadsFilter) params.append('enrichment_status', leadsFilter);
      const res = await axios.get(
        `${API}/api/marketing/campaigns/${campaignId}/leads?${params}`,
        { withCredentials: true }
      );
      setLeads(res.data.leads);
      setLeadsTotal(res.data.total);
      setLeadsStats(res.data.stats);
      setLeadsPage(page);
    } catch (err) {
      toast.error('Failed to load leads');
    } finally {
      setLoadingLeads(false);
    }
  }, [campaignId, leadsSearch, leadsFilter]);

  useEffect(() => { fetchCampaign(); }, [fetchCampaign]);
  useEffect(() => { if (!loading) fetchLeads(1); }, [loading, fetchLeads]);

  // Poll enrichment progress
  useEffect(() => {
    if (!enriching) return;
    enrichPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(
          `${API}/api/marketing/campaigns/${campaignId}/enrich-progress`,
          { withCredentials: true }
        );
        setEnrichProgress(res.data);
        if (res.data.status !== 'enriching') {
          setEnriching(false);
          clearInterval(enrichPollRef.current);
          fetchLeads(leadsPage);
          fetchCampaign();
          const es = res.data.enricher_stats;
          const enricherMsg = es ? ` (Enricher: ${es.exact_matches || 0} exact, ${es.partial_matches || 0} partial)` : '';
          toast.success(`Enrichment complete: ${res.data.enriched} enriched, ${res.data.failed} failed${enricherMsg}`);
        }
      } catch (err) { /* ignore */ }
    }, 2000);
    return () => clearInterval(enrichPollRef.current);
  }, [enriching, campaignId, leadsPage, fetchLeads, fetchCampaign]);

  // ── Handlers ──

  const splitComma = (s) => s.split(',').map(x => x.trim()).filter(Boolean);

  const handleSaveCriteria = async () => {
    const payload = {
      search_criteria: {
        person_titles: splitComma(criteria.person_titles),
        person_locations: splitComma(criteria.person_locations),
        person_seniorities: criteria.person_seniorities,
        person_not_titles: splitComma(criteria.person_not_titles),
        q_organization_name: criteria.q_organization_name || null,
        organization_locations: splitComma(criteria.organization_locations),
        organization_num_employees_ranges: criteria.organization_num_employees_ranges,
        keywords: criteria.keywords || null,
      },
    };
    try {
      await axios.put(`${API}/api/marketing/campaigns/${campaignId}`, payload, { withCredentials: true });
      toast.success('Search criteria saved');
      fetchCampaign();
    } catch (err) {
      toast.error('Failed to save criteria');
    }
  };

  const handleDiscover = async (page = 1) => {
    await handleSaveCriteria();
    setDiscovering(true);
    try {
      const res = await axios.post(
        `${API}/api/marketing/campaigns/${campaignId}/discover`,
        { page, per_page: 25 },
        { withCredentials: true }
      );
      setDiscoveryResult(res.data);
      setDiscoveryPage(page);
      toast.success(`Found ${res.data.inserted} new leads (${res.data.duplicates_crm} CRM duplicates)`);
      fetchLeads(1);
      fetchCampaign();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Discovery failed');
    } finally {
      setDiscovering(false);
    }
  };

  const handleBulkEnrich = async () => {
    setEnriching(true);
    setEnrichProgress(null);
    try {
      await axios.post(
        `${API}/api/marketing/campaigns/${campaignId}/enrich-all`,
        {},
        { withCredentials: true }
      );
      toast.info('Bulk enrichment started — Phase 1: Enricher batch, Phase 2: Pulse fallback (free)');
    } catch (err) {
      toast.error('Failed to start enrichment');
      setEnriching(false);
    }
  };

  const handleCancelEnrich = async () => {
    try {
      await axios.post(
        `${API}/api/marketing/campaigns/${campaignId}/enrich-cancel`,
        {},
        { withCredentials: true }
      );
      toast.info('Cancelling enrichment...');
    } catch (err) { /* ignore */ }
  };

  const handleDeepEnrich = async () => {
    if (!deepEnrichTarget) return;
    setDeepEnriching(true);
    try {
      const res = await axios.post(
        `${API}/api/marketing/leads/${deepEnrichTarget.id}/enrich-deep`,
        {},
        { withCredentials: true }
      );
      toast.success('Deep enrichment complete');
      setDeepEnrichTarget(null);
      fetchLeads(leadsPage);
      fetchCampaign();
    } catch (err) {
      toast.error('Deep enrichment failed');
    } finally {
      setDeepEnriching(false);
    }
  };

  const handlePromote = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setPromoting(true);
    try {
      const res = await axios.post(
        `${API}/api/marketing/leads/promote`,
        { lead_ids: ids },
        { withCredentials: true }
      );
      toast.success(`Promoted ${res.data.promoted} leads to CRM`);
      if (res.data.errors?.length) {
        toast.error(`${res.data.errors.length} leads had errors`);
      }
      setSelected(new Set());
      setSelectAll(false);
      setShowPromote(false);
      fetchLeads(leadsPage);
      fetchCampaign();
    } catch (err) {
      toast.error('Promotion failed');
    } finally {
      setPromoting(false);
    }
  };

  const handleCsvImport = async () => {
    if (!csvText.trim()) return;
    setImporting(true);
    try {
      const lines = csvText.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
      const rows = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim());
        const row = {};
        headers.forEach((h, i) => { row[h] = values[i] || ''; });
        return row;
      });
      const res = await axios.post(
        `${API}/api/marketing/campaigns/${campaignId}/import-csv`,
        { rows },
        { withCredentials: true }
      );
      toast.success(`Imported ${res.data.inserted} leads (${res.data.duplicates} duplicates)`);
      setShowCsvImport(false);
      setCsvText('');
      fetchLeads(1);
      fetchCampaign();
    } catch (err) {
      toast.error('CSV import failed');
    } finally {
      setImporting(false);
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelected(new Set());
    } else {
      setSelected(new Set(leads.filter(l => !l.promoted && !l.duplicate_of_lead_id).map(l => l.id)));
    }
    setSelectAll(!selectAll);
  };

  const toggleSeniority = (val) => {
    const arr = criteria.person_seniorities;
    setCriteria({
      ...criteria,
      person_seniorities: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val],
    });
  };

  const toggleEmployeeRange = (val) => {
    const arr = criteria.organization_num_employees_ranges;
    setCriteria({
      ...criteria,
      organization_num_employees_ranges: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val],
    });
  };

  const enrichBadge = (lead) => {
    const status = lead.enrichment_status;
    const source = lead.enrich_source;
    const matchType = lead.enricher_match_type;
    switch (status) {
      case 'enriched':
        if (source === 'enricher') {
          return (
            <span className="flex items-center gap-1">
              <Badge className="bg-emerald-100 text-emerald-700 text-xs">Enricher</Badge>
              {matchType && (
                <Badge className={`text-xs ${matchType === 'exact' ? 'bg-emerald-50 text-emerald-600' : 'bg-yellow-50 text-yellow-600'}`}>
                  {matchType}
                </Badge>
              )}
            </span>
          );
        }
        return <Badge className="bg-amber-100 text-amber-700 text-xs">Pulse</Badge>;
      case 'enriched_deep': return <Badge className="bg-green-100 text-green-700 text-xs">Full</Badge>;
      case 'failed': return <Badge className="bg-red-100 text-red-700 text-xs">No Data</Badge>;
      case 'pending': return <Badge className="bg-slate-100 text-slate-500 text-xs">Pending</Badge>;
      default: return <Badge className="bg-slate-100 text-slate-500 text-xs">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const totalPages = Math.ceil(leadsTotal / 50);

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/marketing')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{campaign?.name}</h1>
          {campaign?.description && (
            <p className="text-sm text-slate-500">{campaign.description}</p>
          )}
        </div>
        <Badge className={
          campaign?.status === 'enriching' ? 'bg-amber-100 text-amber-700' :
          campaign?.status === 'complete' ? 'bg-green-100 text-green-700' :
          'bg-slate-100 text-slate-600'
        }>
          {campaign?.status}
        </Badge>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {[
          { label: 'Discovered', value: leadsStats.total || 0, color: '' },
          { label: 'Pending', value: leadsStats.pending || 0, color: 'text-slate-500' },
          { label: 'Enriched', value: leadsStats.enriched || 0, color: 'text-amber-600' },
          { label: 'With Email', value: leadsStats.with_email || 0, color: 'text-green-600' },
          { label: 'Promoted', value: leadsStats.promoted || 0, color: 'text-indigo-600' },
          { label: 'Duplicates', value: leadsStats.duplicates || 0, color: 'text-red-500' },
        ].map(({ label, value, color }) => (
          <Card key={label}>
            <CardContent className="p-3 text-center">
              <p className={`text-xl font-bold ${color}`}>{value}</p>
              <p className="text-xs text-slate-500">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search Criteria */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="h-4 w-4" /> Search Criteria (Apollo People Search)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Job Titles (comma-separated)</Label>
              <Input
                placeholder="VP of HR, Director of Talent Acquisition"
                value={criteria.person_titles}
                onChange={(e) => setCriteria({ ...criteria, person_titles: e.target.value })}
              />
            </div>
            <div>
              <Label>Locations (comma-separated)</Label>
              <Input
                placeholder="Texas, United States"
                value={criteria.person_locations}
                onChange={(e) => setCriteria({ ...criteria, person_locations: e.target.value })}
              />
            </div>
            <div>
              <Label>Company Name / Keyword</Label>
              <Input
                placeholder="staffing, recruiting"
                value={criteria.q_organization_name}
                onChange={(e) => setCriteria({ ...criteria, q_organization_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Keywords (free text)</Label>
              <Input
                placeholder="recruiting, hiring"
                value={criteria.keywords}
                onChange={(e) => setCriteria({ ...criteria, keywords: e.target.value })}
              />
            </div>
            <div>
              <Label>Exclude Titles (comma-separated)</Label>
              <Input
                placeholder="Intern, Assistant"
                value={criteria.person_not_titles}
                onChange={(e) => setCriteria({ ...criteria, person_not_titles: e.target.value })}
              />
            </div>
            <div>
              <Label>Company Locations</Label>
              <Input
                placeholder="New York, California"
                value={criteria.organization_locations}
                onChange={(e) => setCriteria({ ...criteria, organization_locations: e.target.value })}
              />
            </div>
          </div>

          {/* Seniority checkboxes */}
          <div>
            <Label className="mb-2 block">Seniority</Label>
            <div className="flex flex-wrap gap-3">
              {SENIORITY_OPTIONS.map(({ value, label }) => (
                <label key={value} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={criteria.person_seniorities.includes(value)}
                    onCheckedChange={() => toggleSeniority(value)}
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Employee ranges */}
          <div>
            <Label className="mb-2 block">Company Size (employees)</Label>
            <div className="flex flex-wrap gap-3">
              {EMPLOYEE_RANGES.map(({ value, label }) => (
                <label key={value} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={criteria.organization_num_employees_ranges.includes(value)}
                    onCheckedChange={() => toggleEmployeeRange(value)}
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <Button onClick={() => handleDiscover(1)} disabled={discovering} className="gap-2">
              {discovering ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              {discovering ? 'Searching...' : 'Discover Leads'}
            </Button>
            {discoveryResult && discoveryResult.apollo_page < discoveryResult.apollo_total_pages && (
              <Button
                variant="outline"
                onClick={() => handleDiscover(discoveryResult.apollo_page + 1)}
                disabled={discovering}
                className="gap-2"
              >
                <ChevronRight className="h-4 w-4" />
                Load More (Page {discoveryResult.apollo_page + 1}/{discoveryResult.apollo_total_pages})
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowCsvImport(true)} className="gap-2">
              <Download className="h-4 w-4" /> Import CSV
            </Button>
            {discoveryResult && (
              <span className="text-sm text-slate-500">
                Apollo: {discoveryResult.apollo_total} total matches
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Enrichment Progress */}
      {enriching && enrichProgress && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">
                    {enrichProgress.phase === 'enricher'
                      ? 'Phase 1: Batch enrichment (100M+ profiles)...'
                      : 'Phase 2: Pulse fallback for remaining leads...'
                    }
                  </p>
                  <p className="text-sm text-amber-600">
                    {enrichProgress.enriched + enrichProgress.failed} / {enrichProgress.total} processed
                    {' '}({enrichProgress.enriched} enriched, {enrichProgress.failed} no data)
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-48 bg-amber-200 rounded-full h-2">
                  <div
                    className="bg-amber-600 h-2 rounded-full transition-all"
                    style={{ width: `${((enrichProgress.enriched + enrichProgress.failed) / Math.max(enrichProgress.total, 1)) * 100}%` }}
                  />
                </div>
                <Button variant="outline" size="sm" onClick={handleCancelEnrich}>Cancel</Button>
              </div>
            </div>
            {/* Enricher batch stats */}
            {enrichProgress.enricher_stats && (
              <div className="flex items-center gap-4 text-xs text-amber-700 bg-amber-100/50 rounded px-3 py-2">
                <span className="font-medium">Enricher Results:</span>
                <span className="text-emerald-700">{enrichProgress.enricher_stats.exact_matches || 0} exact</span>
                <span className="text-yellow-700">{enrichProgress.enricher_stats.partial_matches || 0} partial</span>
                <span className="text-slate-500">{enrichProgress.enricher_stats.not_found || 0} not found</span>
                <span>|</span>
                <span className="text-emerald-700">{enrichProgress.enricher_stats.has_email || 0} w/ email</span>
                <span className="text-blue-700">{enrichProgress.enricher_stats.has_phone || 0} w/ phone</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Lead Table */}
      {(leadsStats.total || 0) > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4" /> Leads ({leadsTotal})
              </CardTitle>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Search leads..."
                  value={leadsSearch}
                  onChange={(e) => setLeadsSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchLeads(1)}
                  className="w-48 h-8 text-sm"
                />
                <select
                  value={leadsFilter}
                  onChange={(e) => { setLeadsFilter(e.target.value); }}
                  className="h-8 text-sm border rounded-md px-2"
                >
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="enriched">Enriched (All)</option>
                  <option value="enriched_enricher">Enriched (Enricher)</option>
                  <option value="enriched_pulse">Enriched (Pulse)</option>
                  <option value="enriched_deep">Enriched (Full)</option>
                  <option value="failed">No Data</option>
                </select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchLeads(1)}
                  className="gap-1"
                >
                  <Filter className="h-3 w-3" /> Filter
                </Button>
                <Button
                  size="sm"
                  onClick={handleBulkEnrich}
                  disabled={enriching || !leadsStats.pending}
                  className="gap-1 bg-amber-600 hover:bg-amber-700"
                >
                  <Sparkles className="h-3 w-3" />
                  Enrich All ({leadsStats.pending} pending)
                </Button>
                {selected.size > 0 && (
                  <Button
                    size="sm"
                    onClick={() => setShowPromote(true)}
                    className="gap-1 bg-indigo-600 hover:bg-indigo-700"
                  >
                    <ArrowUpRight className="h-3 w-3" />
                    Promote ({selected.size})
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="px-3 py-2 text-left w-8">
                      <Checkbox checked={selectAll} onCheckedChange={toggleSelectAll} />
                    </th>
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-left">Title</th>
                    <th className="px-3 py-2 text-left">Company</th>
                    <th className="px-3 py-2 text-left">Location</th>
                    <th className="px-3 py-2 text-left">Email</th>
                    <th className="px-3 py-2 text-left">Phone</th>
                    <th className="px-3 py-2 text-center">Status</th>
                    <th className="px-3 py-2 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr
                      key={lead.id}
                      className={`border-b hover:bg-slate-50 ${lead.promoted ? 'bg-indigo-50/50' : ''} ${lead.duplicate_of_lead_id ? 'bg-red-50/30' : ''}`}
                    >
                      <td className="px-3 py-2">
                        {!lead.promoted && !lead.duplicate_of_lead_id && (
                          <Checkbox
                            checked={selected.has(lead.id)}
                            onCheckedChange={() => toggleSelect(lead.id)}
                          />
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium">
                          {lead.first_name} {lead.last_name}
                        </div>
                        {lead.linkedin_url && (
                          <a
                            href={lead.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-500 hover:underline flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            LinkedIn <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-600 max-w-[180px] truncate">{lead.title}</td>
                      <td className="px-3 py-2 text-slate-600 max-w-[150px] truncate">{lead.company_name}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">
                        {[lead.city, lead.state].filter(Boolean).join(', ')}
                      </td>
                      <td className="px-3 py-2">
                        {lead.work_email ? (
                          <button
                            onClick={() => { navigator.clipboard.writeText(lead.work_email); toast.success('Email copied'); }}
                            className="text-xs text-green-700 hover:underline flex items-center gap-1"
                          >
                            {lead.work_email} <Copy className="h-3 w-3" />
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {lead.work_phone || lead.mobile_phone ? (
                          <button
                            onClick={() => { navigator.clipboard.writeText(lead.work_phone || lead.mobile_phone); toast.success('Phone copied'); }}
                            className="text-xs text-slate-600 hover:underline flex items-center gap-1"
                          >
                            {lead.work_phone || lead.mobile_phone} <Copy className="h-3 w-3" />
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {enrichBadge(lead)}
                          {lead.promoted && (
                            <Badge className="bg-indigo-100 text-indigo-700 text-xs">CRM</Badge>
                          )}
                          {lead.duplicate_of_lead_id && (
                            <Badge className="bg-red-100 text-red-600 text-xs">Dup</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {!lead.promoted && (lead.enrichment_status === 'enriched' || lead.enrichment_status === 'failed') && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              title="Deep Enrich (uses paid credits)"
                              onClick={() => setDeepEnrichTarget(lead)}
                            >
                              <Zap className="h-3.5 w-3.5 text-amber-500" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <span className="text-sm text-slate-500">
                  Page {leadsPage} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={leadsPage <= 1}
                    onClick={() => fetchLeads(leadsPage - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={leadsPage >= totalPages}
                    onClick={() => fetchLeads(leadsPage + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Deep Enrich Confirmation Dialog */}
      <Dialog open={!!deepEnrichTarget} onOpenChange={() => !deepEnriching && setDeepEnrichTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-500" />
              Deep Enrich — Uses Paid Credits
            </DialogTitle>
            <DialogDescription>
              This will use <strong>Apollo + SignalHire + Prospeo</strong> credits to find contact information for{' '}
              <strong>{deepEnrichTarget?.first_name} {deepEnrichTarget?.last_name}</strong>.
              <br /><br />
              Only use this for high-priority leads where the free Pulse enrichment didn't find results.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeepEnrichTarget(null)} disabled={deepEnriching}>
              Cancel
            </Button>
            <Button onClick={handleDeepEnrich} disabled={deepEnriching} className="gap-2 bg-amber-600 hover:bg-amber-700">
              {deepEnriching && <Loader2 className="h-4 w-4 animate-spin" />}
              Use Credits & Enrich
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promote Confirmation Dialog */}
      <Dialog open={showPromote} onOpenChange={() => !promoting && setShowPromote(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpRight className="h-5 w-5 text-indigo-600" />
              Promote to CRM
            </DialogTitle>
            <DialogDescription>
              Promote <strong>{selected.size} leads</strong> to the CRM leads section.
              They will appear as new leads with source "marketing_campaign:{campaign?.name}".
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPromote(false)} disabled={promoting}>Cancel</Button>
            <Button onClick={handlePromote} disabled={promoting} className="gap-2 bg-indigo-600 hover:bg-indigo-700">
              {promoting && <Loader2 className="h-4 w-4 animate-spin" />}
              Promote {selected.size} Leads
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={showCsvImport} onOpenChange={() => !importing && setShowCsvImport(false)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import CSV</DialogTitle>
            <DialogDescription>
              Paste CSV data with headers. Required: <code>linkedin_url</code>.
              Optional: <code>first_name, last_name, title, company_name, city, state</code>.
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            placeholder={`linkedin_url,first_name,last_name,title,company_name\nhttps://www.linkedin.com/in/johndoe,John,Doe,VP of HR,Acme Corp`}
            className="w-full h-48 p-3 border rounded-md text-sm font-mono"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCsvImport(false)} disabled={importing}>Cancel</Button>
            <Button onClick={handleCsvImport} disabled={importing || !csvText.trim()} className="gap-2">
              {importing && <Loader2 className="h-4 w-4 animate-spin" />}
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
